import {
  AnnotationInput,
  AttachmentInput,
  useMedplum,
  useMedplumNavigate,
} from '@medplum/react';
import { FC, useState } from 'react';
import { Annotation, Attachment, DocumentReference } from '@medplum/fhirtypes';
import { createReference } from '@medplum/core';
import { usePatient } from '@hooks/usePatient';
import {
  Button,
  Card,
  Center,
  Container,
  Group,
  Stack,
  Title,
  Image,
  Box,
  CloseButton,
} from '@mantine/core';
import classes from './PatientNewVisit.module.scss';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck } from '@tabler/icons-react';

// -----------------------------------------------------------------------------
export const PatientNewVisit: FC = () => {
  const [images, setImages] = useState<(Attachment | undefined)[]>([undefined]);
  const [annotations, setAnnotations] = useState<(Annotation | undefined)[]>(
    []
  );

  // ---------------------------------------------------------------------------
  const medplum = useMedplum();
  const profile = medplum.getProfile();
  const patient = usePatient();
  const navigate = useMedplumNavigate();

  // ---------------------------------------------------------------------------
  if (!patient || !profile) {
    return null;
  }

  // ---------------------------------------------------------------------------
  const onClickSave = async (): Promise<void> => {
    const documentReferences: DocumentReference[] = [];
    await Promise.all(
      images.map(async (image) => {
        if (image) {
          const doc = await medplum.createResource({
            resourceType: 'DocumentReference',
            status: 'current',
            author: [createReference(profile)],
            subject: createReference(patient),
            content: [
              {
                attachment: image,
              },
            ],
          });

          documentReferences.push(doc);
        }
      })
    );

    await medplum.createResource({
      resourceType: 'Observation',
      subject: createReference(patient),
      performer: [createReference(profile)],
      status: 'final',
      code: {
        text: 'Hair Clinic Visit',
      },
      derivedFrom: documentReferences.map((doc) => createReference(doc)),
      note: annotations
        .filter((annotation) => annotation !== undefined)
        .filter(
          (annotation) =>
            annotation?.text !== undefined && annotation.text !== ''
        ),
    });

    notifications.show({
      title: 'Visit saved',
      message: 'The visit has been saved successfully',
      color: 'green',
      icon: <IconCircleCheck />,
    });

    navigate(`/Patient/${patient.id}/visits`);
  };

  // ---------------------------------------------------------------------------
  return (
    <Container>
      <Stack my="lg" gap="lg">
        <Card>
          <Stack align="flex-start" gap="xl">
            <Title order={2} mx="sm">
              Image Upload
            </Title>
            <Group mx="xl">
              {images
                .filter((i) => i !== undefined)
                .map((image: Attachment | undefined) => {
                  if (!image?.url) {
                    return null;
                  }

                  return (
                    <Center>
                      <Box className={classes.imageWrapper}>
                        <Image
                          radius="md"
                          key={image.id}
                          src={image.url}
                          h={150}
                          fit="fill"
                          w="220"
                          fallbackSrc="https://placehold.co/600x400?text=Placeholder"
                        />
                        <CloseButton
                          className={classes.deleteButton}
                          variant="transparent"
                          onClick={() => {
                            const newImages = images.filter((i) => i !== image);
                            setImages(newImages);
                          }}
                        />
                      </Box>
                    </Center>
                  );
                })}
            </Group>

            <Container mx="0">
              {images.map((image, index) => {
                if (!image) {
                  return (
                    <AttachmentInput
                      key={index}
                      name={`attachment-${index}`}
                      path={`attachment-${index}`}
                      onChange={(attachment) => {
                        const newImages = [...images];
                        newImages[index] = attachment;
                        if (index === images.length - 1) {
                          newImages.push(undefined);
                        }
                        setImages(newImages);
                      }}
                    />
                  );
                }
                return null;
              })}
            </Container>
          </Stack>
        </Card>

        <Card>
          <Stack align="flex-start" gap="xl">
            <Title order={2} ml="md" mb="lg">
              Notes
            </Title>

            <Stack mx="md" w="95%">
              {annotations.map((annotation, index) => (
                <AnnotationInput
                  key={index}
                  defaultValue={annotation}
                  name={`annotation-${index}`}
                  path={'Extension.value[x]'}
                  onChange={(annotation) => {
                    annotations[index] = annotation;
                    setAnnotations([...annotations]);
                  }}
                />
              ))}
            </Stack>

            {annotations.every((annotation) => annotation !== undefined) && (
              <Container mx="0">
                <Button
                  onClick={() => setAnnotations([...annotations, undefined])}
                >
                  Add Note
                </Button>
              </Container>
            )}
          </Stack>
        </Card>

        <Group justify="flex-end">
          <Button onClick={onClickSave}>Save Visit</Button>
        </Group>
      </Stack>
    </Container>
  );
};
